/**
 * El selector de Google Drive (Google Picker) — único contacto con Google
 * del lado del navegador, junto con la metadata (§7.1 del stack).
 *
 * El token lo custodia Clerk y lo entrega /api/drive/token. El Picker se abre
 * acotado a la carpeta del cliente (`setParent`), así RASTRO no puede elegir
 * por error un archivo de otro cliente: no lo ve. `setAppId` hace que lo
 * elegido quede accesible con el permiso `drive.file`, el único que pide el
 * portal.
 */

const API_KEY = import.meta.env.VITE_GOOGLE_API_KEY ?? ''
const APP_ID = import.meta.env.VITE_GOOGLE_APP_ID ?? ''

export const pickerConfigurado = () => Boolean(API_KEY && APP_ID)

export interface ArchivoElegido {
  id: string
  nombre: string
  mime: string
  /** Bytes; Drive no lo informa para documentos de Google. */
  peso: number | null
  url: string
  esCarpeta: boolean
}

export interface OpcionesPicker {
  token: string
  /** Carpeta del cliente: el selector no muestra nada fuera de ella. */
  carpetaId?: string | null
  modo: 'archivos' | 'carpeta'
  multiple?: boolean
  /** Tipos permitidos, p. ej. "image/jpeg,image/png,video/mp4". */
  tipos?: string
  titulo?: string
}

/* ── Tipos mínimos de la API de Google (no hay @types instalados) ── */

interface VistaPicker {
  setParent(id: string): VistaPicker
  setIncludeFolders(v: boolean): VistaPicker
  setSelectFolderEnabled(v: boolean): VistaPicker
  setEnableDrives(v: boolean): VistaPicker
  setMimeTypes(tipos: string): VistaPicker
  setMode(modo: unknown): VistaPicker
  setOwnedByMe(v: boolean): VistaPicker
  /** El nombre de la pestaña; no está en todas las versiones de la API. */
  setLabel?(nombre: string): VistaPicker
}
interface ConstructorPicker {
  addView(v: VistaPicker): ConstructorPicker
  enableFeature(f: unknown): ConstructorPicker
  setOAuthToken(t: string): ConstructorPicker
  setDeveloperKey(k: string): ConstructorPicker
  setAppId(id: string): ConstructorPicker
  setOrigin(o: string): ConstructorPicker
  setLocale(l: string): ConstructorPicker
  setTitle(t: string): ConstructorPicker
  setCallback(cb: (datos: RespuestaPicker) => void): ConstructorPicker
  build(): { setVisible(v: boolean): void }
}
interface DocumentoPicker {
  id: string
  name: string
  mimeType: string
  sizeBytes?: number | string
  url: string
}
interface RespuestaPicker {
  action: string
  docs?: DocumentoPicker[]
}
interface GooglePicker {
  DocsView: new (vista?: unknown) => VistaPicker
  PickerBuilder: new () => ConstructorPicker
  ViewId: { DOCS: unknown; FOLDERS: unknown }
  DocsViewMode: { GRID: unknown; LIST: unknown }
  Feature: { MULTISELECT_ENABLED: unknown; SUPPORT_DRIVES: unknown }
  Action: { PICKED: string; CANCEL: string }
}
/** Los globales que dejan los scripts de Google; pueden no estar todavía. */
const globales = () =>
  window as unknown as {
    gapi?: { load(nombre: string, cb: () => void): void }
    google?: { picker?: GooglePicker }
  }

/* ── Carga de la biblioteca ─────────────────────────────── */

let cargando: Promise<GooglePicker> | null = null

function cargarPicker(): Promise<GooglePicker> {
  cargando ??= new Promise<GooglePicker>((resolver, rechazar) => {
    const listo = () => {
      globales().gapi?.load('picker', () => {
        const p = globales().google?.picker
        if (p) resolver(p)
        else rechazar(new Error('Google Picker no cargó'))
      })
    }
    if (globales().gapi) {
      listo()
      return
    }
    const script = document.createElement('script')
    script.src = 'https://apis.google.com/js/api.js'
    script.async = true
    script.onload = listo
    script.onerror = () => {
      cargando = null
      rechazar(new Error('No se pudo cargar el selector de Google'))
    }
    document.head.appendChild(script)
  })
  return cargando
}

/**
 * Carga la biblioteca antes de que haga falta: al mostrarse un botón de Drive.
 * Así el primer clic no espera la descarga (revisión OO del 23/09: el selector
 * tardaba unos segundos en responder).
 */
export function precargarPicker() {
  if (pickerConfigurado()) cargarPicker().catch(() => undefined)
}

/* ── Convivir con el modal propio ───────────────────────── */

/** Lo que dibuja Google Picker en la página: el fondo y el diálogo. */
const SELECTOR_PICKER = '.picker-dialog, .picker-dialog-bg'

export const esDelPicker = (nodo: unknown) =>
  nodo instanceof Element && Boolean(nodo.closest(SELECTOR_PICKER))

/**
 * El selector se abre casi siempre desde un modal (Radix Dialog), y Google lo
 * agrega al final de la página, fuera de él. El modal hace tres cosas con
 * todo lo que queda afuera, y las tres rompían el selector (revisión OO del
 * 23/09/2026):
 *   - le saca los clics a la página (`pointer-events: none` en el body): el
 *     selector quedaba congelado y el clic caía en el modal de abajo;
 *   - se cierra si el foco se va afuera: al tocar "Seleccionar" se cerraba
 *     el modal de Nuevo recurso o Planificación;
 *   - devuelve el foco adentro: no se podía escribir en la búsqueda.
 * Mientras el selector está abierto, lo suyo recibe clics (CSS) y los avisos
 * de foco que lo involucran no le llegan al modal. Devuelve cómo deshacerlo.
 */
function protegerDelModal(): () => void {
  if (!document.getElementById('estilo-picker')) {
    const estilo = document.createElement('style')
    estilo.id = 'estilo-picker'
    estilo.textContent = `${SELECTOR_PICKER} { pointer-events: auto !important; }`
    document.head.appendChild(estilo)
  }
  const callarFoco = (e: FocusEvent) => {
    if (esDelPicker(e.target) || esDelPicker(e.relatedTarget))
      e.stopImmediatePropagation()
  }
  window.addEventListener('focusin', callarFoco, true)
  window.addEventListener('focusout', callarFoco, true)
  return () => {
    window.removeEventListener('focusin', callarFoco, true)
    window.removeEventListener('focusout', callarFoco, true)
  }
}

/* ── Vistas ─────────────────────────────────────────────── */

const CARPETA = 'application/vnd.google-apps.folder'

/**
 * DEC-12: la carpeta del cliente está en "Mi unidad" de una profesional,
 * compartida con las demás. El selector abre en "Mi unidad", desde la raíz,
 * y ofrece también lo compartido con ella y las unidades compartidas (las de
 * prueba de develop y revisión). Antes abría sólo en las unidades
 * compartidas, y en producción no se encontraba la carpeta.
 */
function vistasDeCarpeta(picker: GooglePicker): VistaPicker[] {
  const carpetas = (vista: unknown, nombre: string) => {
    const v = new picker.DocsView(vista)
      .setIncludeFolders(true)
      .setSelectFolderEnabled(true)
      .setMimeTypes(CARPETA)
      .setMode(picker.DocsViewMode.LIST)
    v.setLabel?.(nombre)
    return v
  }
  return [
    carpetas(picker.ViewId.FOLDERS, 'Mi unidad').setParent('root'),
    carpetas(picker.ViewId.DOCS, 'Compartido conmigo').setOwnedByMe(false),
    carpetas(picker.ViewId.FOLDERS, 'Unidades compartidas').setEnableDrives(true),
  ]
}

/** Archivos de una pieza: abre directo en la carpeta del cliente, esté donde esté. */
function vistaDeArchivos(picker: GooglePicker, o: OpcionesPicker): VistaPicker {
  const vista = (
    o.modo === 'carpeta'
      ? new picker.DocsView(picker.ViewId.FOLDERS)
          .setIncludeFolders(true)
          .setSelectFolderEnabled(true)
          .setMimeTypes(CARPETA)
      : new picker.DocsView(picker.ViewId.DOCS).setIncludeFolders(true)
  )
    .setEnableDrives(true)
    .setMode(picker.DocsViewMode.GRID)
  if (o.carpetaId) vista.setParent(o.carpetaId)
  if (o.tipos && o.modo === 'archivos') vista.setMimeTypes(o.tipos)
  return vista
}

/* ── Abrir ──────────────────────────────────────────────── */

/** Abre el selector y resuelve con lo elegido; `[]` si la persona cancela. */
export async function abrirPicker(o: OpcionesPicker): Promise<ArchivoElegido[]> {
  if (!pickerConfigurado()) throw new Error('El selector de Google no está configurado')
  const picker = await cargarPicker()

  const vistas =
    o.modo === 'carpeta' && !o.carpetaId
      ? vistasDeCarpeta(picker)
      : [vistaDeArchivos(picker, o)]

  const soltar = protegerDelModal()
  return new Promise<ArchivoElegido[]>((resolver) => {
    let constructor = new picker.PickerBuilder()
    for (const v of vistas) constructor = constructor.addView(v)
    constructor = constructor
      .enableFeature(picker.Feature.SUPPORT_DRIVES)
      .setOAuthToken(o.token)
      .setDeveloperKey(API_KEY)
      .setAppId(APP_ID)
      .setOrigin(window.location.origin)
      .setLocale('es')
      .setCallback((datos) => {
        if (
          datos.action === picker.Action.PICKED ||
          datos.action === picker.Action.CANCEL
        )
          soltar()
        if (datos.action === picker.Action.PICKED) {
          resolver(
            (datos.docs ?? []).map((d) => ({
              id: d.id,
              nombre: d.name,
              mime: d.mimeType,
              peso: d.sizeBytes === undefined ? null : Number(d.sizeBytes),
              url: d.url,
              esCarpeta: d.mimeType === 'application/vnd.google-apps.folder',
            })),
          )
        } else if (datos.action === picker.Action.CANCEL) {
          resolver([])
        }
      })
    if (o.titulo) constructor = constructor.setTitle(o.titulo)
    if (o.multiple)
      constructor = constructor.enableFeature(picker.Feature.MULTISELECT_ENABLED)
    constructor.build().setVisible(true)
  })
}

/* ── Metadata (Drive API con el token de RASTRO) ─────────── */

export interface Metadata {
  mime: string
  peso: number | null
  ancho: number | null
  alto: number | null
  /** Sólo video. */
  duracionSeg: number | null
  /** Miniatura que sirve Google: se guarda y evita pedírsela al navegador. */
  miniatura: string | null
}

/** Tipo, peso, medidas y duración. Con `drive.file` sólo vale para lo elegido. */
export async function metadataDe(id: string, token: string): Promise<Metadata> {
  const campos =
    'mimeType,size,thumbnailLink,imageMediaMetadata(width,height),videoMediaMetadata(width,height,durationMillis)'
  const r = await fetch(
    `https://www.googleapis.com/drive/v3/files/${encodeURIComponent(id)}?fields=${encodeURIComponent(campos)}&supportsAllDrives=true`,
    { headers: { Authorization: `Bearer ${token}` } },
  )
  if (!r.ok) throw new Error(`Drive respondió ${String(r.status)}`)
  const m = (await r.json()) as {
    mimeType: string
    size?: string
    thumbnailLink?: string
    imageMediaMetadata?: { width?: number; height?: number }
    videoMediaMetadata?: { width?: number; height?: number; durationMillis?: string }
  }
  const medidas = m.imageMediaMetadata ?? m.videoMediaMetadata
  return {
    mime: m.mimeType,
    peso: m.size ? Number(m.size) : null,
    ancho: medidas?.width ?? null,
    alto: medidas?.height ?? null,
    duracionSeg: m.videoMediaMetadata?.durationMillis
      ? Math.round(Number(m.videoMediaMetadata.durationMillis) / 1000)
      : null,
    // Google la devuelve con un tamaño fijo al final (=s220): se saca para
    // pedirla después al ancho que haga falta.
    miniatura: m.thumbnailLink?.replace(/=s\d+$/, '') ?? null,
  }
}

/* ── Degradado (blurhash) calculado en el navegador de RASTRO ── */

/**
 * El degradado que se ve mientras llega la foto (§8.2). Se calcula acá, una
 * vez, con la imagen que baja Drive con el token de RASTRO, y se guarda.
 * Sólo imágenes: un video usa el de su portada.
 */
export async function blurhashDe(id: string, token: string): Promise<string | null> {
  try {
    const r = await fetch(
      `https://www.googleapis.com/drive/v3/files/${encodeURIComponent(id)}?alt=media&supportsAllDrives=true`,
      { headers: { Authorization: `Bearer ${token}` } },
    )
    if (!r.ok) return null
    const imagen = await createImageBitmap(await r.blob(), { resizeWidth: 32 })
    const ancho = imagen.width
    const alto = imagen.height
    const lienzo = document.createElement('canvas')
    lienzo.width = ancho
    lienzo.height = alto
    const ctx = lienzo.getContext('2d')
    if (!ctx) return null
    ctx.drawImage(imagen, 0, 0)
    const { data } = ctx.getImageData(0, 0, ancho, alto)
    const { encode } = await import('blurhash')
    return encode(data, ancho, alto, 4, 3)
  } catch {
    // Sin degradado la pieza se ve igual, con el fondo neutro.
    return null
  }
}

/* ── Selector simulado, sólo para las pruebas automáticas ── */

/**
 * Las cuentas de prueba no tienen Google, así que el selector real no se
 * puede abrir en los E2E. Fuera de producción, una prueba puede dejar en
 * `window.__driveFalso` lo que "eligió" y su metadata.
 */
export interface DriveFalso {
  elegidos: ArchivoElegido[]
  metadata: Record<string, Metadata>
  /**
   * Abrir el selector de verdad (el código de `abrirPicker`), contra una
   * imitación de Google Picker que la prueba deja en `window.google`. Sirve
   * para probar cómo convive con el modal; lo elegido lo decide la imitación.
   */
  selector?: boolean
}
export function driveFalso(): DriveFalso | null {
  if (import.meta.env.VITE_CONTEXTO === 'production') return null
  return (window as unknown as { __driveFalso?: DriveFalso }).__driveFalso ?? null
}
